<template>
  <div>
    <main class="page__container">
      <div class="container">
        <h2>Редактирование Hardware группы</h2>
        <el-form ref="form" :rules="rules" :model="form">
          <el-row :gutter="20">
            <el-col :span="12">
              <el-form-item label="Имя" prop="name">
                <el-input v-model="form.name" size="small" />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="20">
            <el-col :span="8">
              <server-select
                v-model="selectedMCores"
                :source="sources.mcore"
                :selected-items="initialItems.mcores"
                label="MCores"
                multiple
                :multiple-limit="2"
                show-quota
              />
            </el-col>
            <el-col :span="8">
              <server-select
                v-model="selectedArchCores"
                :source="sources.archcore"
                :selected-items="initialItems.archcores"
                label="ArchCores"
                multiple
              />
            </el-col>
            <el-col :span="8">
              <server-select
                v-model="selectedArchCaster"
                :source="sources.archcaster"
                :selected-items="initialItems.archcasters"
                label="Archive Caster"
                clearable
              />
            </el-col>
          </el-row>
        </el-form>
      </div>
    </main>
    <el-footer class="page__active_bar page__active_bar--spread">
      <el-button v-if="$can('delete', 'hardware_groups')" type="danger" @click="deleteGroup">
        Удалить группу
      </el-button>
      <div class="page__active_buttons">
        <el-button type="info" plain @click="$router.push({ name: 'hw-groups', params: { page: 1 } })">
          Отменить
        </el-button>
        <el-button type="primary" :disabled="!canSave" :loading="fetch" @click="submit">
          Сохранить
        </el-button>
      </div>
    </el-footer>
  </div>
</template>
<script>
import { length, required } from "@/core/helpers/validators";
import { mapActions } from "vuex";
import isEqual from "lodash/isEqual";
import sortBy from "lodash/sortBy";
import ServerSelect from "./ServerSelect";
import { ARCHCASTER_SOURCE, ARCHCORE_SOURCE, MCORE_SOURCE } from "../serverSources";

export default {
  name: "HwGroupEdit",
  components: { ServerSelect },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      fetch: false,
      form: {
        name: "",
      },
      rules: {
        name: [required(), length(3, 200)],
      },
      sources: {
        mcore: MCORE_SOURCE,
        archcore: ARCHCORE_SOURCE,
        archcaster: ARCHCASTER_SOURCE,
      },
      initialItems: {
        mcores: [],
        archcores: [],
        archcasters: [],
      },
      selectedMCores: [],
      selectedArchCores: [],
      selectedArchCaster: null,
      original: null,
    };
  },
  computed: {
    hasChanges() {
      if (!this.original) return false;
      return (
        this.form.name !== this.original.name ||
        !isEqual(sortBy(this.selectedMCores), sortBy(this.original.mcores)) ||
        !isEqual(sortBy(this.selectedArchCores), sortBy(this.original.archcores)) ||
        this.selectedArchCaster !== this.original.archcaster
      );
    },
    canSave() {
      // Сохранить можно только изменённую группу, в которой остались MCore и ArchCore:
      // пустую группу не сохраняем сознательно. ArchCaster при редактировании снять можно.
      return (
        this.$can("update", "hardware_groups") &&
        this.hasChanges &&
        !!this.form.name &&
        this.selectedMCores.length > 0 &&
        this.selectedArchCores.length > 0
      );
    },
  },
  created() {
    this.getHwGroup(this.id)
      .then((group) => {
        this.form.name = group.name;
        this.initialItems = {
          mcores: (group.proxy_servers || []).map((s) => ({ id: s.id_proxy_server, name: s.name })),
          archcores: (group.servers || []).map((s) => ({ id: s.id_server, name: s.name })),
          archcasters: (group.archive_casters || []).map((s) => ({ id: s.id_archive_caster, name: s.name })),
        };
        this.selectedMCores = this.initialItems.mcores.map((s) => s.id);
        this.selectedArchCores = this.initialItems.archcores.map((s) => s.id);
        // API несимметричен: GET отдаёт archive_casters массивом объектов, а POST/PUT
        // принимают одиночное число id_archive_caster. Сохранить можно только один,
        // поэтому берём первый.
        const [firstArchCaster] = this.initialItems.archcasters;
        this.selectedArchCaster = firstArchCaster ? firstArchCaster.id : null;
        this.original = {
          name: group.name,
          mcores: [...this.selectedMCores],
          archcores: [...this.selectedArchCores],
          archcaster: this.selectedArchCaster,
        };
      })
      .catch((e) => {
        const isNotFound = e.response && e.response.status === 404;
        this.$message({
          type: "error",
          message: isNotFound
            ? "Группа не найдена"
            : (e.response && e.response.data.error) || "Не удалось загрузить группу",
        });
        if (isNotFound) this.$router.push({ name: "hw-groups", params: { page: 1 } });
      });
  },
  methods: {
    ...mapActions("hwGroups", {
      getHwGroup: "GET_HW_GROUP",
      updateHwGroup: "UPDATE_HW_GROUP",
      removeHwGroup: "REMOVE_HW_GROUP",
    }),
    submit() {
      let formIsValid;
      this.$refs["form"].validate((status) => {
        formIsValid = status;
      });
      if (!formIsValid || !this.canSave) return false;

      this.fetch = true;
      const newHardwareGroup = {
        name: this.form.name,
        proxy_servers: [...this.selectedMCores],
        servers: [...this.selectedArchCores],
        id_archive_caster: this.selectedArchCaster,
      };

      this.updateHwGroup({ id: this.id, newHardwareGroup })
        .then(() => {
          this.$router.push({ name: "hw-groups", params: { page: 1 } });
          this.$message({
            message: "Изменения сохранены",
            type: "success",
          });
        })
        .catch((e) => {
          this.$message({
            type: "error",
            message: (e.response && e.response.data.error) || "Неизвестная ошибка",
          });
        })
        .finally(() => {
          this.fetch = false;
        });
    },
    deleteGroup() {
      this.$confirm("Удалить Hardware группу?", "Удаление", {
        confirmButtonText: "Удалить",
        cancelButtonText: "Отменить",
        confirmButtonClass: "el-button--danger",
        cancelButtonClass: "el-button--success",
        type: "warning",
      })
        .then(() => this.removeHwGroup(this.id))
        .then(() => {
          this.$router.push({ name: "hw-groups", params: { page: 1 } });
          this.$message({
            message: "Группа удалена",
            type: "success",
          });
        })
        .catch((e) => {
          if (e === "cancel") return;
          this.$message({
            type: "error",
            message: (e.response && e.response.data.error) || "Неизвестная ошибка",
          });
        });
    },
  },
};
</script>
<style scoped>
.page_container {
  padding: 20px;
}
.page__active_bar--spread {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
}
.page__active_buttons {
  display: flex;
  justify-content: flex-end;
  gap: 11px;
  padding: 11px 0;
}
</style>
